import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabaseClient";
import { getFavorites, removeFavorite } from "../../services/favoritesService";
import { getPlayerProfile } from "../../services/playerService";
import { getNotesForPlayer } from "../../services/notesService";
import { createConversation, listConversations } from "../../services/messagesService";
import { VscSearch, VscNote, VscTrash, VscComment, VscPerson } from "react-icons/vsc";
import { POSITION_OPTIONS } from "../../constants/positions";
import "./Favorites.css";

function Favorites() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [favorites, setFavorites] = useState([]);
    const [scoutId, setScoutId] = useState(null);

    // Search & Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedPositions, setSelectedPositions] = useState([]);
    const [sortBy, setSortBy] = useState("recent");
    const [viewMode, setViewMode] = useState("grid");

    // Expanded notes tracking
    const [expandedNotes, setExpandedNotes] = useState({});

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError("");

            const { data: sessionData } = await supabase.auth.getSession();
            const user = sessionData?.session?.user ?? null;
            if (!user) {
                setError("Not signed in.");
                setLoading(false);
                return;
            }

            // Determine role: prefer server-side `user_profiles.role`, fallback to auth metadata
            let effectiveRole = null;
            try {
                const { data: upData, error: upErr } = await supabase
                    .from("user_profiles")
                    .select("role")
                    .eq("user_id", user.id)
                    .maybeSingle();
                if (!upErr && upData && upData.role) {
                    effectiveRole = upData.role.toString().toLowerCase();
                    console.log("Favorites: user_profiles.role for user", user.id, effectiveRole);
                }
            } catch (e) {
                console.error("Favorites: error fetching user_profiles role", e);
            }

            if (!effectiveRole) {
                const metaRole = user?.user_metadata?.role;
                effectiveRole = metaRole ? metaRole.toString().toLowerCase() : null;
                console.log("Favorites: auth metadata role for user", user.id, effectiveRole);
            }

            if (effectiveRole !== "scout" && effectiveRole !== "coach") {
                setError("Favorites are only available for scouts and coaches.");
                setLoading(false);
                return;
            }

            setScoutId(user.id);

            const { data, error: favErr } = await getFavorites(user.id);
            if (favErr) {
                setError(favErr.message);
                setLoading(false);
                return;
            }

            // Hydrate player profiles and fetch notes
            const hydrated = [];
            for (const fav of (data || [])) {
                const { data: player } = await getPlayerProfile(fav.player_id);
                if (player) {
                    // Fetch notes for this player
                    const { data: notes } = await getNotesForPlayer(user.id, fav.player_id);
                    hydrated.push({
                        ...fav,
                        player,
                        notes: notes || []
                    });
                }
            }

            setFavorites(hydrated);
            setLoading(false);
        };
        load();
    }, []);

    // Filter and sort
    const filtered = favorites.filter((fav) => {
        const fullName = `${fav.player.firstname || ""} ${fav.player.lastname || ""}`.toLowerCase();
        const team = (fav.player.team || "").toLowerCase();
        const city = (fav.player.city || "").toLowerCase();
        const query = searchQuery.toLowerCase();

        // Text search
        const matchesSearch = !query ||
            fullName.includes(query) ||
            team.includes(query) ||
            city.includes(query) ||
            (fav.player.positions || []).some(p => p.toLowerCase().includes(query));

        // Position filter
        const matchesPosition = selectedPositions.length === 0 ||
            (fav.player.positions || []).some(p => selectedPositions.includes(p));

        return matchesSearch && matchesPosition;
    });

    const sorted = [...filtered].sort((a, b) => {
        if (sortBy === "recent") return new Date(b.created_at) - new Date(a.created_at);
        if (sortBy === "name") {
            const aName = `${a.player.firstname || ""} ${a.player.lastname || ""}`.trim();
            const bName = `${b.player.firstname || ""} ${b.player.lastname || ""}`.trim();
            return aName.localeCompare(bName);
        }
        if (sortBy === "position") {
            return (a.player.positions?.[0] || "").localeCompare(b.player.positions?.[0] || "");
        }
        if (sortBy === "notes") {
            return b.notes.length - a.notes.length;
        }
        return 0;
    });

    const handleRemove = async (playerId, favId) => {
        if (!window.confirm("Remove this player from favorites?")) return;
        const { error } = await removeFavorite(scoutId, playerId);
        if (!error) {
            setFavorites((prev) => prev.filter((f) => f.id !== favId));
        }
    };

    const handleMessage = async (playerUserId) => {
        if (!scoutId) return;

        // Check for existing conversation
        const { data: conversations } = await listConversations(scoutId);
        const existing = conversations?.find(
            conv => (conv.initiator_id === playerUserId && conv.recipient_id === scoutId) ||
                    (conv.initiator_id === scoutId && conv.recipient_id === playerUserId)
        );

        if (existing) {
            navigate("/messages", { state: { conversationId: existing.id } });
        } else {
            const { data, error } = await createConversation(scoutId, playerUserId);
            if (error) {
                console.error("Failed to create conversation:", error);
                // Optionally surface an error UI later
            } else if (data) {
                navigate("/messages", { state: { conversationId: data.id } });
            }
        }
    };

    const togglePosition = (pos) => {
        setSelectedPositions((prev) =>
            prev.includes(pos) ? prev.filter((p) => p !== pos) : [...prev, pos]
        );
    };

    const clearFilters = () => {
        setSearchQuery("");
        setSelectedPositions([]);
    };

    const toggleNoteExpansion = (favId) => {
        setExpandedNotes(prev => ({ ...prev, [favId]: !prev[favId] }));
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const getAge = (dob) => {
        if (!dob) return null;
        const birth = new Date(dob);
        if (Number.isNaN(birth.getTime())) return null;
        const today = new Date();
        return Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));
    };

    // Stats
    const totalNotes = favorites.reduce((acc, f) => acc + f.notes.length, 0);
    const positionBreakdown = {};
    favorites.forEach(f => {
        (f.player.positions || []).forEach(p => {
            positionBreakdown[p] = (positionBreakdown[p] || 0) + 1;
        });
    });

    if (loading) {
        return (
            <main className="page">
                <div className="favorites-page">
                    <div className="favorites-loading">Loading your favorites...</div>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="page">
                <div className="favorites-page">
                    <div className="favorites-empty">
                        <p>{error}</p>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="page">
            <div className="favorites-page">
                {/* Header */}
                <div className="favorites-header">
                    <div className="favorites-header-top">
                        <h1 className="favorites-title">
                            My Favorites
                        </h1>
                        <div className="favorites-view-toggle">
                            <button
                                className={`favorites-view-btn ${viewMode === "grid" ? "active" : ""}`}
                                onClick={() => setViewMode("grid")}
                                title="Grid View"
                            >
                                ▦
                            </button>
                            <button
                                className={`favorites-view-btn ${viewMode === "list" ? "active" : ""}`}
                                onClick={() => setViewMode("list")}
                                title="List View"
                            >
                                ≡
                            </button>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    {favorites.length > 0 && (
                        <div className="favorites-stats">
                            <div className="favorites-stat">
                                <span className="favorites-stat-value">{favorites.length}</span>
                                <span className="favorites-stat-label">Players</span>
                            </div>
                            <div className="favorites-stat">
                                <span className="favorites-stat-value">{totalNotes}</span>
                                <span className="favorites-stat-label">Notes</span>
                            </div>
                            <div className="favorites-stat">
                                <span className="favorites-stat-value">{Object.keys(positionBreakdown).length}</span>
                                <span className="favorites-stat-label">Positions</span>
                            </div>
                        </div>
                    )}

                    {/* Filters */}
                    <div className="favorites-filters">
                        <div className="favorites-search">
                            <VscSearch className="favorites-search-icon" size={16} />
                            <input
                                type="text"
                                className="favorites-search-input"
                                placeholder="Search by name, team, city, or position..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <select
                            className="favorites-filter-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="recent">Recently Added</option>
                            <option value="name">Name (A-Z)</option>
                            <option value="position">Position</option>
                            <option value="notes">Most Notes</option>
                        </select>

                        <div className="favorites-position-filters">
                            {POSITION_OPTIONS.slice(0, 11).map(({ value, short }) => (
                                <button
                                    key={value}
                                    className={`favorites-position-btn ${selectedPositions.includes(value) ? "selected" : ""}`}
                                    onClick={() => togglePosition(value)}
                                    title={value}
                                >
                                    {short}
                                </button>
                            ))}
                        </div>

                        {(searchQuery || selectedPositions.length > 0) && (
                            <button className="favorites-clear-btn" onClick={clearFilters}>
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Results Info */}
                {favorites.length > 0 && (
                    <div className="favorites-results-info">
                        <span>
                            {filtered.length === favorites.length
                                ? `Showing all ${favorites.length} favorites`
                                : `Showing ${filtered.length} of ${favorites.length} favorites`}
                        </span>
                    </div>
                )}

                {/* Empty State */}
                {favorites.length === 0 ? (
                    <div className="favorites-empty">
                        <h3>No favorites yet</h3>
                        <p>Start exploring players and add them to your favorites for quick access.</p>
                        <Link to="/explore" className="favorites-empty-btn">
                            Explore Players
                        </Link>
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="favorites-empty">
                        <h3>No matches found</h3>
                        <p>Try adjusting your search or filters.</p>
                        <button className="favorites-empty-btn" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>
                ) : (
                    /* Player Cards */
                    <div className={viewMode === "grid" ? "favorites-grid" : "favorites-list"}>
                        {sorted.map(({ id, player, notes, created_at }) => {
                            const fullName = `${player.firstname || ""} ${player.lastname || ""}`.trim() || "Player";
                            const initial = fullName.charAt(0).toUpperCase();
                            const age = getAge(player.DateOfBirth);
                            const latestNote = notes[0];
                            const isExpanded = expandedNotes[id];

                            return (
                                <div key={id} className="favorite-card">
                                    <div className="favorite-card-header">
                                        {player.avatar_url
                                            ? <img src={player.avatar_url} alt={fullName} className="favorite-avatar avatar-img" />
                                            : <div className="favorite-avatar">{initial}</div>
                                        }
                                        <div className="favorite-info">
                                            <h3 className="favorite-name">
                                                {fullName}
                                                {player.verified && <span className="verified-badge-sm">✓</span>}
                                            </h3>
                                            <div className="favorite-meta">
                                                {age && (
                                                    <span className="favorite-meta-item">
                                                        {age} yrs
                                                    </span>
                                                )}
                                                {/* separator */}
                                                    |
                                                {/* dont remove */}
                                                {player.city && (
                                                    <span className="favorite-meta-item">
                                                        {player.city}
                                                    </span>
                                                )}
                                            </div>
                                            {player.positions?.length > 0 && (
                                                <div className="favorite-positions">
                                                    {player.positions.map((pos) => (
                                                        <span key={pos} className="favorite-position-tag">{pos}</span>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="favorite-added-date">
                                                Added {formatDate(created_at)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes Section */}
                                    {notes.length > 0 ? (
                                        <div className="favorite-notes">
                                            <div className="favorite-notes-header">
                                                <VscNote size={14} />
                                                <span>{notes.length} Note{notes.length > 1 ? "s" : ""}</span>
                                            </div>
                                            <div className="favorite-notes-content">
                                                {isExpanded
                                                    ? latestNote.note
                                                    : latestNote.note.length > 120
                                                        ? latestNote.note.substring(0, 120) + "..."
                                                        : latestNote.note
                                                }
                                            </div>
                                            {latestNote.note.length > 120 && (
                                                <span
                                                    className="favorite-notes-more"
                                                    onClick={() => toggleNoteExpansion(id)}
                                                >
                                                    {isExpanded ? "Show less" : "Show more"}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="favorite-no-notes">
                                            No notes yet — view profile to add notes
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="favorite-actions">
                                        <Link
                                            to={`/player/${player.user_id}`}
                                            className="favorite-action-btn favorite-action-primary"
                                        >
                                            <VscPerson style={{ marginRight: "0.3rem" }} /> View Profile
                                        </Link>
                                        <button
                                            className="favorite-action-btn favorite-action-secondary"
                                            onClick={() => handleMessage(player.user_id)}
                                        >
                                            <VscComment style={{ marginRight: "0.3rem" }} /> Message
                                        </button>
                                        <button
                                            className="favorite-action-btn favorite-action-danger"
                                            onClick={() => handleRemove(player.id, id)}
                                            title="Remove from favorites"
                                        >
                                            <VscTrash />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </main>
    );
}

export default Favorites;
